import React, { useEffect, useState, useCallback } from 'react'
import { DialogTitle, DialogContent, DialogActions, Button, Slide, Box, Chip, Avatar, Popover } from '@mui/material'
import { TransitionProps } from '@mui/material/transitions'
import { GridCloseIcon } from '@mui/x-data-grid'
import AskBar from './AskBar'
import { ChatArea, type ChatElement} from './ChatArea'
import { Message } from './Message'
import { bubbleText, computingBubble } from './Bubble'
import { v4 as uuidv4 } from 'uuid'
import { useQuery } from 'react-query'
import axios from 'axios'
import { useSkin } from '@hooks/useSkin'

interface OptimusProps {
  onClose: () => void,
  open: boolean,
  anchorEl: function,
  logo: string
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />
})

const userAvatar = localStorage.getItem("avatar")
import defaultAvatar from '@src/assets/images/avatars/avatar_light.png'

const OptimusDialog = ({ onClose, open, logo: optimusAvatar, anchorEl }: OptimusProps) => {
  const [messages, setMessages] = useState([])
  const [chatElements, setChatElements] = useState([])
  const [askingOptimus, setAskingOptimus] = useState(false)
  const [currentQuestion, setCurrentQuestion] = useState(undefined)

  const computeOptimus = useCallback(async () => {
    return (await axios.get(`https://optimus.optimahr.app/?ask=${currentQuestion.text}`)).data
    // return (await axios.get(`http://localhost:8888/?ask=${currentQuestion.text}`)).data
  }, [currentQuestion])

  const { skin } = useSkin()

  function handleClose() {
    onClose()
  }

  function newQuestion(question: string) {
    const questionId = uuidv4()
    const newMessage: Message = {
      id: questionId,
      time: new Date(),
      from: 'me',
      text: question
    }

    setMessages([...messages, newMessage])
    setChatElements([...chatElements, bubbleText(newMessage, userAvatar || defaultAvatar, skin === 'dark')])
    setCurrentQuestion(newMessage)
    setAskingOptimus(true)
  }

  function newAnswer(answer: string, questionId: string) {
    const newMessage: Message = {
      id: uuidv4(),
      time: new Date(),
      from: 'optimus',
      text: answer,
      questionId
    }

    setMessages([...messages, newMessage])
    setChatElements([...chatElements.filter((e) => e.content.key !== 'computing'), bubbleText(newMessage, optimusAvatar)])
  }

  const newLoadingAnswer = useCallback(() => {
    if (chatElements[chatElements.length - 1].content.key !== 'computing') {
      setTimeout(() => {
        setChatElements([...chatElements, computingBubble(optimusAvatar)])
      }, 300)
    }
  }, [chatElements, optimusAvatar])


  const { refetch: askOptimus } = useQuery('optimus', computeOptimus, {
    enabled: false,
    retry: false,
    onSettled: (data, error) => {
      newAnswer(data ? data : error ? error.message : 'Error', currentQuestion.id)
      setCurrentQuestion(undefined)
    }
  })

  useEffect(() => {
    if (askingOptimus) {
      askOptimus()
      setAskingOptimus(false)
      newLoadingAnswer()
    }
  }, [askingOptimus, newLoadingAnswer, askOptimus])

  useEffect(() => {
    if (optimusAvatar !== undefined) {
      newAnswer(
        localStorage.getItem('i18nextLng') === 'fr-FR' ? "Demandez-moi ce que vous voulez sur Optima ou sur le monde RH. Je ferai de mon mieux pour vous répondre !" : "Ask me anything you want about your data in OptimAdmin. I'll do my best for answer your questions!",
        '-1'
      )
    }
  }, [optimusAvatar])

  return (
    anchorEl && anchorEl.target === undefined &&
    <Popover
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={(_e, reason) => {
        reason !== 'backdropClick' ? handleClose() : console.log()
      }}
      aria-describedby="alert-dialog-slide-description"
      scroll="paper"
      hideBackdrop
      disableScrollLock
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left'
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left'
      }}
      sx={{maxWidth: '60%', maxHeight: '80vh', marginTop: '20px'}}
      PaperProps={{
        sx: {
          maxHeight: '80vh',
          position: 'absolute'
        }
      }}
    >
      <DialogTitle sx={{backgroundColor: skin === 'dark' ? '#283046' : 'white' }}> 
        <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: skin === 'dark' ? '#283046' : 'white' }}>
          <Box className="actions-lef" sx={{ display: 'flex', alignItems: 'center', backgroundColor: skin === 'dark' ? '#283046' : 'white' }}>
            {localStorage.getItem('i18nextLng') === 'fr-FR' ? 'Optimus, votre assistant préféré !' : 'Optimus, your favorite assistant!'}
          </Box>
          <Box className="actions-right" sx={{ display: 'flex', alignItems: 'center', backgroundColor: skin === 'dark' ? '#283046' : 'white' }}>
            <Button onClick={handleClose} color="error">
              <GridCloseIcon />
            </Button>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent
        style={{
          maxHeight: '80vh', backgroundColor: skin === 'dark' ? '#161D31' : 'white', marginTop: '-7px', paddingTop: '5px'
        }}
      >
        <ChatArea elements={chatElements} sx={{backgroundColor: skin === 'dark' ? '#283046' : 'white'}}/>
      </DialogContent>
      <DialogActions sx={{backgroundColor: skin === 'dark' ? '#161D31' : 'white' }}>
        <AskBar callback={newQuestion} theme={skin} />
      </DialogActions>
    </Popover>
  ) || <></>
}

export default OptimusDialog
